import { useState, useEffect, useRef } from "react";
import type { CapiEventMapping } from "@shared/schema";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "wouter";
import { apiRequest } from "@/lib/queryClient";
import { buildCapiConfigPayload } from "@/lib/capi-config-payload";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";
import {
  Plus,
  Pencil,
  Trash2,
  TestTube,
  Settings,
  Activity,
  ArrowLeft,
  Send,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
  Eye,
  EyeOff,
  Filter,
  RefreshCw,
} from "lucide-react";

// ─── Meta OAuth (VP-226): kształty odpowiedzi backendu używane w tym widoku ───

type MetaOauthStatus = {
  connected: boolean;
  tokenExpired?: boolean;
};

type CapiMetaPixel = {
  id: string;
  name: string;
  adAccountId: string;
  adAccountName: string | null;
};

type CapiMetaPixelsResponse = {
  connected: boolean;
  tokenExpired: boolean;
  pixels: CapiMetaPixel[];
  truncated: boolean;
};

/** Wiersz `capi_send_log` zwracany przez GET /api/capi/logs. */
/** GET /api/capi/config — wiersz capi_platform_config; tokeny przychodza zamaskowane. */
type CapiPlatformConfigRow = {
  id: string;
  platform: string;
  enabled: boolean | null;
  metaPixelId: string | null;
  metaAccessToken: string | null;
  metaTestEventCode: string | null;
  googleCustomerId: string | null;
  googleConversionActionId: string | null;
  tiktokPixelId: string | null;
  tiktokAccessToken: string | null;
  tiktokTestEventCode: string | null;
};

/** GET /api/capi/stats — ksztalt z capiDispatchService.getStats(). */
type CapiStats = {
  total: number;
  sent: number;
  failed: number;
  skipped: number;
  byPlatform: Record<string, { sent: number; failed: number }>;
  recentErrors: Array<{
    platform: string;
    event: string | null;
    error: string | null;
    sentAt: string | null;
  }>;
};

/** GET /api/capi/available-events — katalog zdarzen + presety UI. */
type CapiAvailableEvents = {
  internalEvents: Array<{
    value: string;
    label: string;
    description: string;
    count30d: number;
  }>;
  platformEvents: Record<string, Array<{ value: string; label: string }>>;
  suggestedMappings: Record<string, Record<string, string>>;
  attributionModels: Array<{ value: string; label: string; description: string }>;
  neuroflowWindowPresets: Array<{ value: number; label: string; hint: string }>;
};

/** POST /api/capi/test — wynik capiDispatchService.sendTestEvent(). */
type CapiTestResult = {
  success: boolean;
  httpStatus?: number;
  errorCode?: string;
  error?: string;
};

type CapiSendLogRow = {
  id: string;
  sentAt: string | null;
  platform: string;
  platformEvent: string;
  status: string;
  httpStatus: number | null;
  errorMessage: string | null;
  responseBody: string | null;
};

// Platform display info — Meta is the only supported CAPI destination.
const PLATFORMS: Record<string, { name: string; subline: string; color: string; icon: string }> = {
  meta: { name: "Meta", subline: "Facebook & Instagram", color: "bg-blue-500", icon: "📘" },
};

export default function CapiConfigPage({
  onBack,
  forUserId = null,
}: {
  onBack?: () => void;
  forUserId?: string | null;
}) {
  const { t } = useTranslation("capiConfig");
  const { toast } = useToast();
  const qc = useQueryClient();
  const [activeTab, setActiveTab] = useState("mappings");
  const [addMappingOpen, setAddMappingOpen] = useState(false);
  // Mapowanie w trybie edycji (null = dialog zamknięty). Reużywa AddMappingDialog
  // z propem `initial`, submit idzie przez updateMapping (PUT).
  const [editingMapping, setEditingMapping] = useState<CapiEventMapping | null>(null);
  const [configPlatform, setConfigPlatform] = useState<string | null>(null);
  // Log otwarty w modalu szczegółów (null = zamknięty).
  const [logDetails, setLogDetails] = useState<CapiSendLogRow | null>(null);

  // ─── Agency tenancy: every CAPI call carries the selected client's userId so
  // the backend scopes mappings/configs/logs to that client. Without it, an
  // agency owner would see (and mutate) their own account's mappings while
  // pretending to be in a client's context.
  const qs = forUserId ? `forUserId=${encodeURIComponent(forUserId)}` : "";
  const withQs = (path: string, extra?: string) => {
    const parts = [extra, qs].filter(Boolean).join("&");
    return parts ? `${path}?${parts}` : path;
  };
  const withForUserId = (payload: Record<string, unknown>) =>
    forUserId ? { ...payload, forUserId } : payload;

  // ─── Data Fetching ───

  const { data: mappings = [], isLoading: mappingsLoading } = useQuery<CapiEventMapping[]>({
    queryKey: ["capi-mappings", forUserId],
    queryFn: () => apiRequest("GET", withQs("/api/capi/mappings")),
    staleTime: 30_000,
  });

  const { data: configs = [], isLoading: configsLoading } = useQuery<CapiPlatformConfigRow[]>({
    queryKey: ["capi-configs", forUserId],
    queryFn: () => apiRequest("GET", withQs("/api/capi/config")),
    staleTime: 30_000,
  });

  const { data: logs = [], isLoading: logsLoading } = useQuery<CapiSendLogRow[]>({
    queryKey: ["capi-logs", forUserId],
    queryFn: () => apiRequest("GET", withQs("/api/capi/logs", "limit=50")),
    staleTime: 15_000,
    enabled: activeTab === "logs",
  });

  const { data: stats } = useQuery<CapiStats>({
    queryKey: ["capi-stats", forUserId],
    queryFn: () => apiRequest("GET", withQs("/api/capi/stats", "days=7")),
    staleTime: 30_000,
  });

  const { data: availableEvents } = useQuery<CapiAvailableEvents>({
    queryKey: ["capi-available-events", forUserId],
    queryFn: () => apiRequest("GET", withQs("/api/capi/available-events")),
    staleTime: 300_000,
  });

  // ─── Meta OAuth (VP-226): status połączenia + lista pixeli z konta Meta ───

  const { data: metaOauth, isLoading: metaOauthLoading } = useQuery<MetaOauthStatus>({
    queryKey: ["capi-meta-oauth", forUserId],
    queryFn: () => apiRequest("GET", withQs("/api/integrations/meta/status")),
    staleTime: 60_000,
  });

  const metaOauthReady = !!metaOauth?.connected && !metaOauth?.tokenExpired;

  // Pixels are also needed by mapping-level routing, so load them for both
  // the mappings and platform configuration tabs.
  const {
    data: metaPixels,
    isLoading: metaPixelsLoading,
    isError: metaPixelsError,
  } = useQuery<CapiMetaPixelsResponse>({
    queryKey: ["capi-meta-pixels", forUserId],
    queryFn: () => apiRequest("GET", withQs("/api/capi/meta/pixels")),
    staleTime: 60_000,
    enabled: metaOauthReady && (activeTab === "platforms" || activeTab === "mappings"),
  });

  // Popup OAuth — ten sam wzorzec co meta-connect-panel: puste okno otwierane
  // synchronicznie (anty popup-blocker), URL doklejany po odpowiedzi backendu,
  // ekran ładowania w popupie + polling zamknięcia jako fallback, gdyby
  // postMessage z callbacku przepadło (COOP/zamknięte okno) — review #8.
  const metaPopupRef = useRef<Window | null>(null);
  const connectMeta = useMutation({
    mutationFn: () => apiRequest("GET", withQs("/api/integrations/meta/connect")),
    onSuccess: (data: { url: string }) => {
      if (metaPopupRef.current && !metaPopupRef.current.closed) {
        metaPopupRef.current.location.href = data.url;
      }
    },
    onError: () => {
      metaPopupRef.current?.close();
      toast({
        title: t("toast.retryTitle"),
        description: t("toast.retryDescription"),
        variant: "destructive",
      });
    },
  });

  const invalidateMetaOauthQueries = () => {
    qc.invalidateQueries({ queryKey: ["capi-meta-oauth", forUserId] });
    qc.invalidateQueries({ queryKey: ["capi-meta-pixels", forUserId] });
  };

  const handleMetaConnect = () => {
    const w = 600;
    const h = 700;
    const left = window.screenX + (window.outerWidth - w) / 2;
    const top = window.screenY + (window.outerHeight - h) / 2;
    metaPopupRef.current = window.open(
      "about:blank",
      "meta_oauth",
      `width=${w},height=${h},left=${left},top=${top},toolbar=no,menubar=no`
    );
    if (!metaPopupRef.current) {
      // Popup-blocker: bez tego klik kończył się cichym no-opem.
      toast({
        title: t("metaOauth.popupBlockedTitle"),
        description: t("metaOauth.popupBlockedDescription"),
        variant: "destructive",
      });
      return;
    }
    metaPopupRef.current.document.write(
      `<html><head><title>Meta</title></head><body style="background:#111;color:#fff;display:flex;align-items:center;justify-content:center;height:100vh;margin:0;font-family:sans-serif"><p>${t("metaOauth.popupLoading")}</p></body></html>`
    );
    metaPopupRef.current.document.close();
    const checkInterval = setInterval(() => {
      if (metaPopupRef.current?.closed) {
        clearInterval(checkInterval);
        invalidateMetaOauthQueries();
      }
    }, 500);
    connectMeta.mutate();
  };

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.data?.type === "META_OAUTH_SUCCESS") {
        invalidateMetaOauthQueries();
      }
      if (event.data?.type === "META_OAUTH_ERROR") {
        toast({
          title: t("metaOauth.oauthErrorTitle"),
          description: String(event.data.error ?? ""),
          variant: "destructive",
        });
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qc, forUserId]);

  // Banner re-auth: config Meta istnieje, jedzie na OAuth (brak ręcznego
  // tokenu), a połączenie z Meta nie działa → wysyłka CAPI stoi.
  const metaConfig = configs.find((c) => c.platform === "meta");
  const metaUsesOauth = !!metaConfig && !metaConfig.metaAccessToken;
  const metaOauthBroken =
    metaUsesOauth && !!metaOauth && (!metaOauth.connected || !!metaOauth.tokenExpired);

  // ─── Mutations ───

  const createMapping = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiRequest("POST", "/api/capi/mappings", withForUserId(data)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["capi-mappings", forUserId] });
      setAddMappingOpen(false);
      toast({ title: t("toast.mappingAdded") });
    },
    onError: (err) =>
      toast({
        title: t("toast.retryTitle"),
        description: t("toast.retryDescription"),
        variant: "destructive",
      }),
  });

  const updateMapping = useMutation({
    // PUT przyjmuje id + dowolny podzbior pol mapowania (Switch wysyla caly wiersz,
    // formularz edycji tylko zmienione pola).
    mutationFn: ({ id, ...data }: { id: string } & Record<string, unknown>) =>
      apiRequest("PUT", `/api/capi/mappings/${id}`, withForUserId(data)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["capi-mappings", forUserId] });
      setEditingMapping(null);
      toast({ title: t("toast.mappingUpdated") });
    },
    onError: (err) =>
      toast({
        title: t("toast.retryTitle"),
        description: t("toast.retryDescription"),
        variant: "destructive",
      }),
  });

  const deleteMapping = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", withQs(`/api/capi/mappings/${id}`)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["capi-mappings", forUserId] });
      toast({ title: t("toast.mappingDeleted") });
    },
    onError: (err) =>
      toast({
        title: t("toast.retryTitle"),
        description: t("toast.retryDescription"),
        variant: "destructive",
      }),
  });

  const saveConfig = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiRequest("POST", "/api/capi/config", withForUserId(data)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["capi-configs", forUserId] });
      toast({ title: t("toast.configSaved") });
    },
    onError: (err) =>
      toast({
        title: t("toast.retryTitle"),
        description: t("toast.retryDescription"),
        variant: "destructive",
      }),
  });

  const testEvent = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiRequest("POST", "/api/capi/test", withForUserId(data)),
    onSuccess: (result: CapiTestResult) => {
      if (result.success) {
        toast({ title: t("toast.testSuccess"), description: `HTTP ${result.httpStatus}` });
      } else {
        // errorCode → lokalizowany komunikat; surowy result.error tylko jako
        // fallback dla kodów, których nie znamy (review #16).
        const description = result.errorCode
          ? t(`testErrors.${result.errorCode}`, { defaultValue: result.error })
          : result.error;
        toast({ title: t("toast.testFailed"), description, variant: "destructive" });
      }
    },
    onError: (err) =>
      toast({
        title: t("toast.retryTitle"),
        description: t("toast.retryDescription"),
        variant: "destructive",
      }),
  });

  // ─── Stats Overview ───

  const totalSent = stats?.sent || 0;
  const totalFailed = stats?.failed || 0;
  const totalEvents = stats?.total || 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {onBack && (
            <Button variant="ghost" size="sm" onClick={onBack}>
              <ArrowLeft className="w-4 h-4 mr-1" /> {t("back")}
            </Button>
          )}
          <div>
            <h2 className="text-heading-2 font-extrabold tracking-tight">{t("title")}</h2>
            <p className="text-muted-foreground">{t("description")}</p>
          </div>
        </div>
      </div>

      {/* Stats Cards — compact KPI tiles. Numbers are bold but the labels stay
          small so the icon, copy, and value never wrap or shove each other. */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-500 shrink-0" />
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground truncate">
                {t("stats.sent")}
              </p>
              <p className="text-xl font-bold text-green-600 leading-tight">{totalSent}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <XCircle className="w-5 h-5 text-red-500 shrink-0" />
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground truncate">
                {t("stats.errors")}
              </p>
              <p className="text-xl font-bold text-red-600 leading-tight">{totalFailed}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <Settings className="w-5 h-5 text-blue-500 shrink-0" />
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground truncate">
                {t("stats.activeMappings")}
              </p>
              <p className="text-xl font-bold leading-tight">
                {mappings.filter((m) => m.enabled).length}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <Activity className="w-5 h-5 text-purple-500 shrink-0" />
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground truncate">
                {t("stats.platforms")}
              </p>
              <p className="text-xl font-bold leading-tight">
                {configs.filter((c) => c.enabled).length}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ─── Banner re-auth Meta (VP-226): OAuth padł → CAPI stoi ─── */}
      {metaOauthBroken && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          {/* /status zwraca connected = !expired (nigdy oba naraz), więc stan
              "wygasło" rozpoznajemy po tokenExpired, nie po connected (review #2). */}
          <AlertTitle>
            {metaOauth?.tokenExpired
              ? t("metaOauth.banner.expiredTitle")
              : t("metaOauth.banner.notConnectedTitle")}
          </AlertTitle>
          <AlertDescription className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <span>
              {metaOauth?.tokenExpired
                ? t("metaOauth.banner.expiredDescription")
                : t("metaOauth.banner.notConnectedDescription")}
            </span>
            <Button
              variant="outline"
              size="sm"
              className="shrink-0"
              onClick={handleMetaConnect}
              disabled={connectMeta.isPending}
            >
              {connectMeta.isPending ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-1" />
              )}
              {t("metaOauth.banner.reauthButton")}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="mappings">{t("tabs.mappings")}</TabsTrigger>
          <TabsTrigger value="platforms">{t("tabs.platforms")}</TabsTrigger>
          <TabsTrigger value="logs">{t("tabs.logs")}</TabsTrigger>
        </TabsList>

        {/* ─── Event Mappings Tab ─── */}
        <TabsContent value="mappings" className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">{t("mappings.description")}</p>
            <Dialog open={addMappingOpen} onOpenChange={setAddMappingOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="w-4 h-4 mr-2" /> {t("mappings.addButton")}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <AddMappingDialog
                  availableEvents={availableEvents}
                  metaPixels={metaPixels?.pixels ?? []}
                  onSubmit={(data) => createMapping.mutate(data)}
                  isLoading={createMapping.isPending}
                  forUserId={forUserId}
                />
              </DialogContent>
            </Dialog>
            {/* Edycja istniejącego mapowania — ten sam formularz w trybie `initial`.
                key wymusza remount przy zmianie mapowania, żeby prefill zadziałał. */}
            <Dialog
              open={!!editingMapping}
              onOpenChange={(open) => {
                if (!open) setEditingMapping(null);
              }}
            >
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                {editingMapping && (
                  <AddMappingDialog
                    key={editingMapping.id}
                    availableEvents={availableEvents}
                    metaPixels={metaPixels?.pixels ?? []}
                    onSubmit={(data) => updateMapping.mutate({ id: editingMapping.id, ...data })}
                    isLoading={updateMapping.isPending}
                    forUserId={forUserId}
                    initial={editingMapping}
                  />
                )}
              </DialogContent>
            </Dialog>
          </div>

          {mappingsLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : mappings.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Send className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">{t("mappings.emptyTitle")}</h3>
                <p className="text-muted-foreground mb-4">{t("mappings.emptyDescription")}</p>
                <Button onClick={() => setAddMappingOpen(true)}>
                  <Plus className="w-4 h-4 mr-2" /> {t("mappings.addFirst")}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("mappings.table.internalEvent")}</TableHead>
                    <TableHead>{t("mappings.table.platform")}</TableHead>
                    <TableHead>{t("mappings.table.platformEvent")}</TableHead>
                    <TableHead>{t("mappings.table.sendRevenue")}</TableHead>
                    <TableHead>{t("mappings.table.sendUserData")}</TableHead>
                    <TableHead>{t("mappings.table.status")}</TableHead>
                    <TableHead className="text-right">{t("mappings.table.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {mappings.map((mapping) => (
                    <TableRow key={mapping.id}>
                      <TableCell className="font-medium">{mapping.internalEvent}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="gap-1">
                          <span>{PLATFORMS[mapping.platform]?.icon}</span>
                          {PLATFORMS[mapping.platform]?.name || mapping.platform}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <code className="text-sm">
                          {mapping.customEventName || mapping.platformEvent}
                        </code>
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={mapping.sendRevenue ?? undefined}
                          onCheckedChange={(checked) =>
                            updateMapping.mutate({
                              ...mapping,
                              sendRevenue: checked,
                            })
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={mapping.sendUserData ?? undefined}
                          onCheckedChange={(checked) =>
                            updateMapping.mutate({
                              ...mapping,
                              sendUserData: checked,
                            })
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={mapping.enabled ?? undefined}
                          onCheckedChange={(checked) =>
                            updateMapping.mutate({
                              ...mapping,
                              enabled: checked,
                            })
                          }
                        />
                      </TableCell>
                      <TableCell className="text-right space-x-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditingMapping(mapping)}
                          title={t("editMappingDialog.editButton")}
                          aria-label={t("editMappingDialog.editButton")}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            testEvent.mutate({ platform: mapping.platform, mappingId: mapping.id })
                          }
                          disabled={testEvent.isPending}
                        >
                          <TestTube className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            if (confirm(t("mappings.confirmDelete"))) {
                              deleteMapping.mutate(mapping.id);
                            }
                          }}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>

        {/* ─── Platform Config Tab ─── */}
        <TabsContent value="platforms" className="space-y-4">
          <p className="text-sm text-muted-foreground">{t("platformsTab.description")}</p>

          {/* Single platform today (Meta) — keep card sized like a dialog so it
              doesn't drown in whitespace. Grid scales to 2/3 cols if/when we
              re-add destinations. */}
          <div
            className={
              Object.keys(PLATFORMS).length === 1
                ? "max-w-md"
                : "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
            }
          >
            {Object.entries(PLATFORMS).map(([key, platform]) => {
              const config = configs.find((c) => c.platform === key);
              return (
                <PlatformConfigCard
                  key={key}
                  platformKey={key}
                  platform={platform}
                  config={config}
                  onSave={(data) => saveConfig.mutate(data)}
                  onTest={() => testEvent.mutate({ platform: key })}
                  isSaving={saveConfig.isPending}
                  isTesting={testEvent.isPending}
                  metaOauth={metaOauth}
                  metaOauthLoading={metaOauthLoading}
                  metaPixels={metaPixels}
                  metaPixelsLoading={metaPixelsLoading}
                  metaPixelsError={metaPixelsError}
                  onMetaConnect={handleMetaConnect}
                  isConnectingMeta={connectMeta.isPending}
                />
              );
            })}
          </div>
        </TabsContent>

        {/* ─── Logs Tab ─── */}
        <TabsContent value="logs" className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">{t("logs.description")}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => qc.invalidateQueries({ queryKey: ["capi-logs"] })}
            >
              {t("logs.refresh")}
            </Button>
          </div>

          {logsLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : logs.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                {t("logs.empty")}
              </CardContent>
            </Card>
          ) : (
            <Card>
              {/* Jeden provider na tabelę — nie per wiersz. */}
              <TooltipProvider>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("logs.table.date")}</TableHead>
                      <TableHead>{t("logs.table.platform")}</TableHead>
                      <TableHead>{t("logs.table.event")}</TableHead>
                      <TableHead>{t("logs.table.status")}</TableHead>
                      <TableHead>{t("logs.table.http")}</TableHead>
                      <TableHead>{t("logs.table.details")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logs.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell className="text-sm">
                          {log.sentAt ? new Date(log.sentAt).toLocaleString("pl-PL") : "-"}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="gap-1">
                            <span>{PLATFORMS[log.platform]?.icon}</span>
                            {PLATFORMS[log.platform]?.name || log.platform}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <code className="text-sm">{log.platformEvent}</code>
                        </TableCell>
                        <TableCell>
                          {log.status === "sent" && (
                            <Badge className="bg-green-500 text-white">
                              {t("logs.statuses.sent")}
                            </Badge>
                          )}
                          {log.status === "failed" && (
                            <Badge variant="destructive">{t("logs.statuses.failed")}</Badge>
                          )}
                          {log.status === "pending" && (
                            <Badge variant="secondary">{t("logs.statuses.pending")}</Badge>
                          )}
                          {log.status === "skipped" && (
                            <Badge variant="outline">{t("logs.statuses.skipped")}</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {log.httpStatus ? (
                            <code
                              className={`text-sm ${log.httpStatus < 300 ? "text-green-600" : "text-red-600"}`}
                            >
                              {log.httpStatus}
                            </code>
                          ) : (
                            "-"
                          )}
                        </TableCell>
                        <TableCell className="max-w-[240px] text-sm text-muted-foreground">
                          <div className="flex items-center gap-2">
                            <span className="truncate">
                              {log.errorMessage || log.responseBody || "-"}
                            </span>
                            {(log.errorMessage || log.responseBody) && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 shrink-0"
                                    onClick={() => setLogDetails(log)}
                                    aria-label={t("logs.details.view")}
                                  >
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>{t("logs.details.view")}</TooltipContent>
                              </Tooltip>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TooltipProvider>
            </Card>
          )}

          <LogDetailsDialog log={logDetails} onClose={() => setLogDetails(null)} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ─── Log Details Dialog ───

/**
 * Surowa odpowiedź platformy przychodzi jako string. Gdy jest JSON-em —
 * pokazujemy go z wcięciami (czytelne `fbtrace_id`, `messages[]`), a gdy nie —
 * oddajemy tekst bez zmian zamiast wyświetlać "[object Object]" albo pustkę.
 */
function formatResponseBody(raw: string): string {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

function LogDetailsDialog({ log, onClose }: { log: CapiSendLogRow | null; onClose: () => void }) {
  const { t } = useTranslation("capiConfig");

  return (
    <Dialog open={log !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t("logs.details.title")}</DialogTitle>
          <DialogDescription>
            {log?.platformEvent} ·{" "}
            {log?.sentAt ? new Date(log.sentAt).toLocaleString("pl-PL") : "-"}
            {log?.httpStatus ? ` · HTTP ${log.httpStatus}` : ""}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {log?.errorMessage && (
            <div className="space-y-1.5">
              <Label>{t("logs.details.error")}</Label>
              <pre className="max-h-40 overflow-auto rounded-md bg-muted p-3 text-xs whitespace-pre-wrap break-all">
                {log.errorMessage}
              </pre>
            </div>
          )}
          {log?.responseBody && (
            <div className="space-y-1.5">
              <Label>{t("logs.details.response")}</Label>
              <pre className="max-h-80 overflow-auto rounded-md bg-muted p-3 text-xs whitespace-pre-wrap break-all">
                {formatResponseBody(log.responseBody)}
              </pre>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t("logs.details.close")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Add Mapping Dialog ───

function AddMappingDialog({
  availableEvents,
  metaPixels,
  onSubmit,
  isLoading,
  forUserId = null,
  initial = null,
}: {
  availableEvents: CapiAvailableEvents | undefined;
  metaPixels: CapiMetaPixel[];
  onSubmit: (data: Record<string, unknown>) => void;
  isLoading: boolean;
  forUserId?: string | null;
  // Gdy podane → tryb edycji: formularz startuje z wartości istniejącego
  // mapowania, tytuł/CTA zmieniają się, a submit zachowuje enabled/sendRevenue/
  // sendUserData zamiast wymuszać true (nie chcemy re-aktywować wyłączonego).
  initial?: CapiEventMapping | null;
}) {
  const { t } = useTranslation("capiConfig");
  const isEdit = !!initial;
  // When the catalog has exactly one destination we pre-select it so the user
  // doesn't stare at a "Wybierz event platformy" dropdown that's empty
  // because no platform was chosen yet. Today that means Meta, but the rule
  // generalises if we re-add destinations later.
  const platformKeys = Object.keys(PLATFORMS);
  const defaultPlatform = platformKeys.length === 1 ? platformKeys[0] : "";
  const [platform, setPlatform] = useState(initial?.platform ?? defaultPlatform);
  const [internalEvent, setInternalEvent] = useState(initial?.internalEvent ?? "");
  const [platformEvent, setPlatformEvent] = useState(initial?.platformEvent ?? "");
  // Custom event name (override) — pozwala wyslac do Mety np. "QualifiedLead" zamiast
  // standardowego "Lead". Glowny use case: klient ma juz wlasny pixel ktory wystrzela
  // "Lead" → my server-side wysylamy custom event by uniknac duplikatu. Klient potem
  // w Meta Events Manager robi Custom Conversion z trigger event_name = nasz custom.
  const [customEventName, setCustomEventName] = useState(initial?.customEventName ?? "");
  const [pixelId, setPixelId] = useState(initial?.pixelId ?? "");
  // Default to NeuroFlow — it's the model we recommend and what reporting
  // uses; falling back to last_click would silently disagree with dashboards.
  const [attributionSource, setAttributionSource] = useState<
    "first_click" | "last_click" | "neuroflow"
  >((initial?.attributionSource ?? "neuroflow") as "first_click" | "last_click" | "neuroflow");
  // 30 days matches Meta's default click attribution window. Only consulted
  // when attributionSource === 'neuroflow'.
  const [neuroflowWindowDays, setNeuroflowWindowDays] = useState<number>(
    initial?.neuroflowWindowDays ?? 30
  );

  // ─── Optional filters (attribution-parity rules) ──────────────────────────────
  // Backend enforces these via capi-filters.ts; UI just exposes them. All
  // toggles start OFF and tag inputs start empty — the user opts in to each
  // filter explicitly. No "smart defaults" because they hide behavior.
  const [minRevenue, setMinRevenue] = useState<string>(
    initial?.minRevenue != null ? String(initial.minRevenue) : ""
  );
  const [targetCurrency, setTargetCurrency] = useState(initial?.targetCurrency ?? "PLN");
  const [requiredTagsRaw, setRequiredTagsRaw] = useState<string>(
    (initial?.requiredTags ?? []).join(", ")
  );
  const [excludedTagsRaw, setExcludedTagsRaw] = useState<string>(
    (initial?.excludedTags ?? []).join(", ")
  );
  // Sensible quality-of-life defaults — chronia CAPI przed smieciowym ruchem.
  // onlyFirstConversion zostaje OFF, bo psuje e-com z LTV (kazdy powtorny purchase byly ignorowany).
  const [ignoreOrganic, setIgnoreOrganic] = useState<boolean>(initial?.ignoreOrganic ?? true);
  const [onlyFirstConversion, setOnlyFirstConversion] = useState<boolean>(
    initial?.onlyFirstConversion ?? false
  );
  const [excludeTestEmails, setExcludeTestEmails] = useState<boolean>(
    initial?.excludeTestEmails ?? true
  );
  // VP-240: domyślnie ON — bez tego filtry pytają wyłącznie touch wskazany przez
  // model atrybucji i gubią konwersje z reklam przy ruchu mieszanym.
  const [anyTouchClickId, setAnyTouchClickId] = useState<boolean>(initial?.anyTouchClickId ?? true);
  const [matchPlatformAttribution, setMatchPlatformAttribution] = useState<boolean>(
    initial?.matchPlatformAttribution ?? true
  );
  // Auto-suggest platform event ONLY when the user actually changes
  // internalEvent/platform — never on mount (preserves edit prefill) ani gdy
  // async `availableEvents` doładuje się po otwarciu dialogu (inaczej podpowiedź
  // nadpisałaby istniejący platformEvent w trybie edycji). Klucz startowy = stan
  // początkowy, więc pierwszy przebieg i późniejszy load danych są no-opem.
  const lastSuggestKey = useRef(`${internalEvent}|${platform}`);
  useEffect(() => {
    const key = `${internalEvent}|${platform}`;
    if (key === lastSuggestKey.current) return;
    lastSuggestKey.current = key;
    if (internalEvent && platform && availableEvents?.suggestedMappings) {
      const suggested = availableEvents.suggestedMappings[internalEvent]?.[platform];
      if (suggested) setPlatformEvent(suggested);
    }
  }, [internalEvent, platform, availableEvents]);

  const handleSubmit = () => {
    if (!platform || !internalEvent || !platformEvent) return;

    // Tags come from a comma/newline-separated textarea — normalize before
    // sending so the JSONB column stores clean arrays. Empty arrays are
    // serialized as null so they don't trigger the "tag filter active" code
    // path on the backend (which would reject every conversion).
    const parseTagList = (raw: string): string[] | null => {
      const tags = raw
        .split(/[,\n]/)
        .map((t) => t.trim())
        .filter((t) => t.length > 0);
      return tags.length > 0 ? tags : null;
    };

    const minRev = minRevenue.trim() === "" ? null : parseFloat(minRevenue);

    onSubmit({
      platform,
      internalEvent,
      platformEvent,
      customEventName: customEventName.trim() || null,
      pixelId: pixelId || null,
      // Edycja: nie ruszaj stanu włączenia ani wysyłki revenue/user-data
      // (te są sterowane osobnymi togglami w wierszu). Tworzenie: domyślnie ON.
      enabled: initial ? initial.enabled : true,
      sendRevenue: initial ? initial.sendRevenue : true,
      sendUserData: initial ? initial.sendUserData : true,
      attributionSource,
      // Edycja: jeśli mapping miał NULL (=„użyj domyślnej usługi", schema) i user
      // nie ruszył okna (nadal 30), zachowaj NULL zamiast przypinać jawne 30 —
      // inaczej mapping odpina się od ewentualnej przyszłej zmiany domyślnej.
      neuroflowWindowDays:
        attributionSource !== "neuroflow"
          ? null
          : isEdit && initial?.neuroflowWindowDays == null && neuroflowWindowDays === 30
            ? null
            : neuroflowWindowDays,
      // Filters — explicit null when unused keeps DB clean and disables the
      // corresponding filter branch in capi-filters.ts.
      minRevenue: Number.isFinite(minRev as number) ? minRev : null,
      targetCurrency: minRev == null ? null : targetCurrency,
      requiredTags: parseTagList(requiredTagsRaw),
      excludedTags: parseTagList(excludedTagsRaw),
      ignoreOrganic,
      onlyFirstConversion,
      excludeTestEmails,
      matchPlatformAttribution,
      anyTouchClickId,
    });
  };

  // If we have catalog events but ALL of them have count30d === 0, the user
  // hasn't received any conversions in 30 days — likely missing tracking snippet
  // or no GHL/Shopify webhooks wired. Mapping CAPI is fine but won't fire anything.
  const internalEventsList = (availableEvents?.internalEvents ?? []) as Array<{
    count30d?: number;
  }>;
  const hasAnyConversions = internalEventsList.some((e) => (e.count30d ?? 0) > 0);
  const showNoTrackingWarning = internalEventsList.length > 0 && !hasAnyConversions;

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          {isEdit ? t("editMappingDialog.title") : t("addMappingDialog.title")}
        </DialogTitle>
        <DialogDescription>
          {isEdit ? t("editMappingDialog.description") : t("addMappingDialog.description")}
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4 py-4">
        {showNoTrackingWarning && (
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/[0.06] p-3 space-y-2">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
              <div className="space-y-1.5 text-[12px] leading-relaxed">
                <p className="text-amber-200 font-semibold">
                  Brak konwersji z trackingu w ostatnich 30 dniach
                </p>
                <p className="text-white/70">
                  CAPI forwarduje konwersje z naszego trackingu do Meta/Google. Zeby cokolwiek
                  polecialo, musi byc <strong>zrodlo eventow</strong>:
                </p>
                <ul className="text-white/60 space-y-0.5 pl-3 list-disc marker:text-amber-400/60">
                  <li>
                    <strong>JS snippet</strong> na stronie (rekomendowane — pelna atrybucja przez{" "}
                    <code>fbclid</code>)
                  </li>
                  <li>
                    <strong>GHL webhook</strong> (dziala, ale bez <code>fbclid</code> match quality
                    slabszy + filter "click_id matchuje Meta" odrzuci eventy)
                  </li>
                  <li>
                    <strong>Shopify / e-com webhook</strong> dla zakupow
                  </li>
                </ul>
                <p className="text-white/50 text-[11px] pt-0.5">
                  Mozesz stworzyc mapping teraz — wystartuje jak tylko pierwszy event wpadnie.
                </p>
              </div>
            </div>
          </div>
        )}
        {/* Platform picker only shows when there's an actual choice. With one
            destination it's busy-work; the static badge keeps users oriented. */}
        {platformKeys.length > 1 ? (
          <div className="space-y-2">
            <Label>{t("addMappingDialog.platformLabel")}</Label>
            <Select value={platform} onValueChange={setPlatform}>
              <SelectTrigger>
                <SelectValue placeholder={t("addMappingDialog.platformPlaceholder")} />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(PLATFORMS).map(([key, p]) => (
                  <SelectItem key={key} value={key}>
                    {p.icon} {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : (
          <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-muted/50 text-sm">
            <span className="text-muted-foreground">Platforma:</span>
            <span className="text-xl">{PLATFORMS[platform]?.icon}</span>
            <strong>{PLATFORMS[platform]?.name}</strong>
            {PLATFORMS[platform]?.subline && (
              <span className="text-muted-foreground text-xs">— {PLATFORMS[platform].subline}</span>
            )}
          </div>
        )}

        <div className="space-y-2">
          <Label>Typ konwersji z Twojego trackingu</Label>
          <Select value={internalEvent} onValueChange={setInternalEvent}>
            <SelectTrigger>
              <SelectValue placeholder={t("addMappingDialog.internalEventPlaceholder")} />
            </SelectTrigger>
            <SelectContent>
              {availableEvents?.internalEvents?.map((e) => {
                // count30d comes from the user's own tracking_conversions in
                // the last 30 days. Dimming + "0" badge makes it obvious which
                // events haven't actually fired yet, so users don't map
                // theoretical events and wonder why nothing arrives.
                const count = e.count30d ?? 0;
                return (
                  <SelectItem key={e.value} value={e.value}>
                    <span className={count === 0 ? "opacity-60" : ""}>
                      {e.label}
                      <span className="text-muted-foreground text-xs ml-2">— {e.description}</span>
                    </span>
                    <span
                      className={`ml-2 text-[11px] px-1.5 py-0.5 rounded ${count === 0 ? "bg-muted text-muted-foreground" : "bg-green-500/15 text-green-500"}`}
                    >
                      {count === 0 ? "brak danych" : `${count} w 30 dni`}
                    </span>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            Lista typów konwersji obsługiwanych przez nasz tracking. Licznik pokazuje ile takich
            konwersji złapaliśmy w ciągu ostatnich 30 dni — jeśli widzisz "brak danych", upewnij się
            że snippet trackingu wysyła ten event.
          </p>
        </div>

        <div className="space-y-2">
          <Label>{t("addMappingDialog.platformEventLabel")}</Label>
          <Select value={platformEvent} onValueChange={setPlatformEvent}>
            <SelectTrigger>
              <SelectValue placeholder={t("addMappingDialog.platformEventPlaceholder")} />
            </SelectTrigger>
            <SelectContent>
              {platform &&
                availableEvents?.platformEvents?.[platform]?.map((e) => (
                  <SelectItem key={e.value} value={e.value}>
                    {e.label}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>

        {/* Custom event name override — chroni przed duplikacja z wlasnym pixelem klienta.
            Np. browser pixel klienta strzela "Lead", my server-side wysylamy "QualifiedLead". */}
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            Custom event name{" "}
            <span className="text-[10px] uppercase tracking-widest text-white/40">
              (override, opcjonalne)
            </span>
          </Label>
          <Input
            value={customEventName}
            onChange={(e) => setCustomEventName(e.target.value)}
            placeholder={platformEvent ? `np. Qualified${platformEvent}` : "np. QualifiedLead"}
          />
          <div
            className={`text-[11px] rounded-md p-2 ${customEventName.trim() ? "border border-emerald-500/30 bg-emerald-500/[0.05] text-emerald-200/90" : "border border-amber-500/20 bg-amber-500/[0.04] text-amber-100/80"}`}
          >
            {customEventName.trim() ? (
              <>
                ✓ Wyślemy do Mety jako{" "}
                <code className="bg-white/10 px-1 rounded">{customEventName.trim()}</code>.
                <br />
                <span className="text-white/60">
                  W Meta Events Manager utwórz <b>Custom Conversion</b> z regulą{" "}
                  <code className="bg-white/10 px-1 rounded">
                    event_name = {customEventName.trim()}
                  </code>{" "}
                  i optymalizuj kampanie na tę konwersję.
                </span>
              </>
            ) : (
              <>
                ⚠ Bez override Meta dostanie standardowy event{" "}
                <code className="bg-white/10 px-1 rounded">{platformEvent || "..."}</code>.
                <br />
                <span className="text-white/60">
                  Jezeli klient ma juz wlasny{" "}
                  <code className="bg-white/10 px-1 rounded">
                    fbq('track','{platformEvent || "..."}')
                  </code>{" "}
                  na landingu → <b>duplikat w Mecie</b>. Wpisz custom (np.{" "}
                  <code className="bg-white/10 px-1 rounded">
                    Qualified{platformEvent || "Lead"}
                  </code>
                  ) zeby tego uniknac.
                </span>
              </>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <Label>Model atrybucji</Label>
          <Select
            value={attributionSource}
            onValueChange={(v) => setAttributionSource(v as typeof attributionSource)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(
                availableEvents?.attributionModels ?? [
                  {
                    value: "neuroflow",
                    label: "NeuroFlow",
                    description:
                      "Hybryda: szybka konwersja → first click; długi funnel → last click.",
                  },
                  {
                    value: "last_click",
                    label: "Last click",
                    description: "Kredytuje ostatnie źródło ruchu przed konwersją.",
                  },
                  {
                    value: "first_click",
                    label: "First click",
                    description: "Kredytuje pierwsze źródło, które przyciągnęło użytkownika.",
                  },
                ]
              ).map((m) => (
                <SelectItem key={m.value} value={m.value}>
                  {m.value === "neuroflow" && "🧠 "}
                  {m.label}
                  <span className="text-muted-foreground text-xs ml-2">— {m.description}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {attributionSource === "neuroflow" && (
          <div className="space-y-2">
            <Label>Okno NeuroFlow</Label>
            <Select
              value={String(neuroflowWindowDays)}
              onValueChange={(v) => setNeuroflowWindowDays(parseInt(v, 10))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(
                  availableEvents?.neuroflowWindowPresets ?? [
                    {
                      value: 7,
                      label: "7 dni",
                      hint: "Szybkie konwersje (impuls, B2C low-ticket)",
                    },
                    { value: 14, label: "14 dni", hint: "Średni cykl decyzyjny" },
                    { value: 30, label: "30 dni", hint: "Domyślny — równy oknu Meta" },
                    { value: 60, label: "60 dni", hint: "Dłuższy funnel B2B" },
                    { value: 90, label: "90 dni", hint: "High-ticket / sales cycle" },
                  ]
                ).map((w) => (
                  <SelectItem key={w.value} value={String(w.value)}>
                    {w.label}
                    <span className="text-muted-foreground text-xs ml-2">— {w.hint}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Konwersja w ciągu {neuroflowWindowDays} dni od pierwszego touchpointu → kredyt do{" "}
              <strong>first click</strong>. Powyżej → <strong>last click</strong>.
            </p>
          </div>
        )}

        {platform === "meta" && (
          <div className="space-y-2">
            <Label>{t("addMappingDialog.destinationPixelLabel")}</Label>
            <Select
              value={pixelId || "__default"}
              onValueChange={(value) => setPixelId(value === "__default" ? "" : value)}
            >
              <SelectTrigger>
                <SelectValue placeholder={t("addMappingDialog.destinationPixelPlaceholder")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__default">
                  {t("addMappingDialog.destinationPixelDefault")}
                </SelectItem>
                {metaPixels.map((pixel) => (
                  <SelectItem key={pixel.id} value={pixel.id}>
                    {pixel.name} · {pixel.adAccountName || pixel.adAccountId}
                  </SelectItem>
                ))}
                {pixelId && !metaPixels.some((pixel) => pixel.id === pixelId) && (
                  <SelectItem value={pixelId}>{pixelId}</SelectItem>
                )}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {t("addMappingDialog.destinationPixelHint")}
            </p>
          </div>
        )}

        {/* ─── Filtry (opcjonalne) — collapsed by default so the dialog stays
            short for the 90% case. Power users open it to keep junk leads out
            of Meta's optimization signal. */}
        <Accordion type="single" collapsible className="border rounded-md">
          <AccordionItem value="filters" className="border-0">
            <AccordionTrigger className="px-3 py-2 hover:no-underline">
              <span className="flex items-center gap-2 text-sm font-medium">
                <Filter className="w-4 h-4" /> Filtry (opcjonalne)
              </span>
            </AccordionTrigger>
            <AccordionContent className="px-3 pb-3 space-y-4">
              <p className="text-xs text-muted-foreground">
                Decydują które konwersje faktycznie lecą do Meta. Brak filtra = wszystko leci.
                Eventy odrzucone trafiają do <strong>Logów wysyłek</strong> ze statusem{" "}
                <em>skipped</em> + powodem.
              </p>

              {/* Min revenue */}
              <div className="space-y-1.5">
                <Label className="text-xs">{t("addMappingDialog.minRevenueLabel")}</Label>
                <div className="grid grid-cols-[1fr_120px] gap-2">
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder={t("addMappingDialog.minRevenuePlaceholder")}
                    value={minRevenue}
                    onChange={(e) => setMinRevenue(e.target.value)}
                  />
                  <Select value={targetCurrency} onValueChange={setTargetCurrency}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {["PLN", "EUR", "USD", "GBP"].map((currency) => (
                        <SelectItem key={currency} value={currency}>
                          {currency}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <p className="text-[11px] text-muted-foreground">
                  {t("addMappingDialog.minRevenueHint")}
                </p>
              </div>

              {/* Required tags — multi-select from user-defined taxonomy. */}
              {/* Tags how-to — minimal setup, pull-mode auto-pobiera tagi z GHL */}
              <div className="rounded-md border border-emerald-500/25 bg-emerald-500/[0.04] p-2.5 space-y-1.5">
                <p className="text-[11px] font-semibold text-emerald-200">
                  ✨ Tagi z GHL — automatycznie, bez konfiguracji
                </p>
                <ol className="text-[11px] text-white/70 leading-relaxed space-y-1 list-decimal pl-4 marker:text-emerald-400/70">
                  <li>
                    W{" "}
                    <a href="/tags" className="text-emerald-300 hover:underline">
                      Zarzadzaj tagami
                    </a>{" "}
                    stworz tag o <strong>dokladnie tej samej nazwie</strong> co masz w GHL (np.{" "}
                    <code className="text-white/85">qualified</code> jak w GHL kontakt ma tag{" "}
                    <code className="text-white/85">qualified</code>).
                  </li>
                  <li>
                    Tutaj w <strong>Wymagane tagi</strong> ponizej zaznacz ten tag.
                  </li>
                  <li>
                    <strong>Gotowe.</strong> Apka przy kazdej konwersji{" "}
                    <strong>sama pociaga tagi kontaktu z GHL przez API</strong> i sprawdza. Jak
                    kontakt ma <code className="text-white/85">qualified</code> w GHL → leci do
                    Mety. Jak nie → <em>skipped</em>.
                  </li>
                </ol>
                <p className="text-[10px] text-white/40 pt-0.5">
                  Bez webhookow w GHL, bez konfiguracji workflow. Wystarczy OAuth (juz polaczone) +
                  zgodne nazwy tagow. Cache 5 min zeby nie spamic GHL API.
                </p>
              </div>

              <TagMultiSelect
                label="Wymagane tagi"
                hint="Wyślij tylko jeśli konwersja ma którykolwiek z zaznaczonych tagów."
                forUserId={forUserId}
                value={requiredTagsRaw}
                onChange={setRequiredTagsRaw}
              />

              {/* Excluded tags */}
              <TagMultiSelect
                label="Wykluczone tagi"
                hint="Odrzuć jeśli konwersja ma którykolwiek z zaznaczonych tagów."
                forUserId={forUserId}
                value={excludedTagsRaw}
                onChange={setExcludedTagsRaw}
              />

              {/* Toggle filters */}
              <div className="flex items-start justify-between gap-3 py-1">
                <div className="space-y-0.5">
                  <Label className="text-xs">Pomiń ruch organiczny</Label>
                  <p className="text-[11px] text-muted-foreground">
                    Konwersje bez click_id (np. direct / organic) nie polecą do Meta.
                  </p>
                </div>
                <Switch checked={ignoreOrganic} onCheckedChange={setIgnoreOrganic} />
              </div>

              <div className="flex items-start justify-between gap-3 py-1">
                <div className="space-y-0.5">
                  <Label className="text-xs">Tylko pierwsza konwersja per visitor</Label>
                  <p className="text-[11px] text-muted-foreground">
                    Wyłapuje powtarzających się klientów — drugi i kolejny purchase nie poleci.
                    Przydatne dla lead-genu, NIE dla e-comu z LTV.
                  </p>
                </div>
                <Switch checked={onlyFirstConversion} onCheckedChange={setOnlyFirstConversion} />
              </div>

              <div className="flex items-start justify-between gap-3 py-1">
                <div className="space-y-0.5">
                  <Label className="text-xs">Pomiń testowe emaile</Label>
                  <p className="text-[11px] text-muted-foreground">
                    Odrzuca leady z emaili typu <code>test@</code>, <code>qa@</code>,{" "}
                    <code>+test@</code>, mailinator/yopmail/10minutemail. Chroni przed bot/QA
                    traffic.
                  </p>
                </div>
                <Switch checked={excludeTestEmails} onCheckedChange={setExcludeTestEmails} />
              </div>

              <div className="flex items-start justify-between gap-3 py-1">
                <div className="space-y-0.5">
                  <Label className="text-xs">Tylko gdy click_id matchuje Meta</Label>
                  <p className="text-[11px] text-muted-foreground">
                    Wyślij tylko jeśli click_id konwersji to <code>fbclid</code>. Zapobiega
                    wysyłaniu Google-atrybuowanych leadów do Meta CAPI (i odwrotnie).
                  </p>
                </div>
                <Switch
                  checked={matchPlatformAttribution}
                  onCheckedChange={setMatchPlatformAttribution}
                />
              </div>

              <div className="flex items-start justify-between gap-3 py-1">
                <div className="space-y-0.5">
                  <Label className="text-xs">Reklama gdziekolwiek w ścieżce</Label>
                  <p className="text-[11px] text-muted-foreground">
                    Wyślij, gdy click_id jest na <strong>którymkolwiek</strong> wejściu klienta, a
                    nie tylko na tym wskazanym przez model atrybucji. Bez tego zakup klienta, który
                    najpierw trafił na sklep organicznie, a dopiero potem kliknął reklamę, zostanie
                    odrzucony jako organiczny. Zalecane włączone.
                  </p>
                </div>
                <Switch checked={anyTouchClickId} onCheckedChange={setAnyTouchClickId} />
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>

      <DialogFooter>
        <Button
          onClick={handleSubmit}
          disabled={!platform || !internalEvent || !platformEvent || isLoading}
        >
          {isLoading ? (
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
          ) : isEdit ? (
            <Pencil className="w-4 h-4 mr-2" />
          ) : (
            <Plus className="w-4 h-4 mr-2" />
          )}
          {isEdit ? t("editMappingDialog.submitButton") : t("addMappingDialog.submitButton")}
        </Button>
      </DialogFooter>
    </>
  );
}

// ─── Platform Config Card ───

function PlatformConfigCard({
  platformKey,
  platform,
  config,
  onSave,
  onTest,
  isSaving,
  isTesting,
  metaOauth,
  metaOauthLoading,
  metaPixels,
  metaPixelsLoading,
  metaPixelsError,
  onMetaConnect,
  isConnectingMeta,
}: {
  platformKey: string;
  platform: { name: string; subline?: string; color: string; icon: string };
  config: CapiPlatformConfigRow | undefined;
  onSave: (data: Record<string, unknown>) => void;
  onTest: () => void;
  isSaving: boolean;
  isTesting: boolean;
  metaOauth?: MetaOauthStatus;
  metaOauthLoading?: boolean;
  metaPixels?: CapiMetaPixelsResponse;
  metaPixelsLoading?: boolean;
  metaPixelsError?: boolean;
  onMetaConnect?: () => void;
  isConnectingMeta?: boolean;
}) {
  const { t } = useTranslation("capiConfig");
  const [editing, setEditing] = useState(false);
  const [showTokens, setShowTokens] = useState(false);
  // Wszystkie pola formularza są tekstowe — puste "" znaczy "brak wartości".
  const [formData, setFormData] = useState<Record<string, string>>({});

  const metaOauthReady = !!metaOauth?.connected && !metaOauth?.tokenExpired;
  const pixelOptions = metaPixels?.pixels ?? [];
  // Zapisany ręcznie Pixel ID spoza listy z API — pokaż go jako dodatkową
  // opcję, żeby Select nie "gubił" aktualnej wartości.
  const currentPixelOutsideList =
    !!formData.metaPixelId && !pixelOptions.some((p) => p.id === formData.metaPixelId);

  useEffect(() => {
    if (config) {
      setFormData({
        metaPixelId: config.metaPixelId || "",
        metaAccessToken: "",
        metaTestEventCode: config.metaTestEventCode || "",
        googleCustomerId: config.googleCustomerId || "",
        googleConversionActionId: config.googleConversionActionId || "",
        tiktokPixelId: config.tiktokPixelId || "",
        tiktokAccessToken: "",
        tiktokTestEventCode: config.tiktokTestEventCode || "",
      });
    }
  }, [config]);

  // Wydzielone, bo przycisk "usuń ręczny token" też musi zapisać bieżący stan
  // formularza (inaczej gubił świeżo wybrany pixel — review #10).
  const buildSaveData = () => buildCapiConfigPayload({ platform: platformKey, formData });

  const handleSave = () => {
    onSave(buildSaveData());
    setEditing(false);
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        {/* `items-start` keeps the badge anchored to the top instead of
            floating mid-card when the title wraps. `gap-3` reserves spacing
            so the badge never collides with the title text. */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-2xl shrink-0">{platform.icon}</span>
            <div className="min-w-0">
              <CardTitle className="text-base leading-tight">{platform.name}</CardTitle>
              {platform.subline && (
                <p className="text-xs text-muted-foreground mt-0.5">{platform.subline}</p>
              )}
            </div>
          </div>
          {config?.enabled ? (
            <Badge className="bg-green-500 text-white shrink-0">{t("platformCard.active")}</Badge>
          ) : (
            <Badge variant="secondary" className="shrink-0">
              {t("platformCard.inactive")}
            </Badge>
          )}
        </div>
        <CardDescription className="pt-2">
          {platformKey === "meta" && t("platformCard.descriptions.meta")}
          {platformKey === "google_ads" && t("platformCard.descriptions.google_ads")}
          {platformKey === "tiktok" && t("platformCard.descriptions.tiktok")}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {!editing ? (
          <>
            {config ? (
              <div className="space-y-2 text-sm">
                {platformKey === "meta" && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Pixel ID:</span>
                      <span>{config.metaPixelId || t("platformCard.notSet")}</span>
                    </div>
                    {/* VP-226: źródło autoryzacji — ręczny token albo konto Meta (OAuth) */}
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t("metaOauth.sourceLabel")}:</span>
                      {config.metaAccessToken ? (
                        <span>
                          {t("metaOauth.sourceManual")} ({config.metaAccessToken})
                        </span>
                      ) : metaOauthReady ? (
                        <span className="text-green-500 flex items-center gap-1">
                          <CheckCircle className="w-3.5 h-3.5" /> {t("metaOauth.sourceOauth")}
                        </span>
                      ) : metaOauth?.tokenExpired ? (
                        <span className="text-destructive">{t("metaOauth.expired")}</span>
                      ) : (
                        <span>{t("platformCard.notSet")}</span>
                      )}
                    </div>
                    {config.metaTestEventCode && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Test Event Code:</span>
                        <span className="text-orange-500">{config.metaTestEventCode}</span>
                      </div>
                    )}
                  </>
                )}
                {platformKey === "google_ads" && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Customer ID:</span>
                      <span>{config.googleCustomerId || t("platformCard.notSet")}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Conversion Action:</span>
                      <span className="truncate max-w-[150px]">
                        {config.googleConversionActionId || t("platformCard.notSet")}
                      </span>
                    </div>
                  </>
                )}
                {platformKey === "tiktok" && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Pixel ID:</span>
                      <span>{config.tiktokPixelId || t("platformCard.notSet")}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Access Token:</span>
                      <span>{config.tiktokAccessToken || t("platformCard.notSet")}</span>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">{t("platformCard.notConfigured")}</p>
            )}

            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                size="sm"
                className="flex-1"
                onClick={() => setEditing(true)}
              >
                <Settings className="w-4 h-4 mr-1" /> {t("platformCard.configureButton")}
              </Button>
              {config && (
                <Button variant="outline" size="sm" onClick={onTest} disabled={isTesting}>
                  {isTesting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <TestTube className="w-4 h-4" />
                  )}
                </Button>
              )}
            </div>
          </>
        ) : (
          <div className="space-y-3">
            {platformKey === "meta" && (
              <>
                {/* ─── VP-226: OAuth jako domyślne źródło — pixel z listy, zero wklejania.
                     Stany rozpoznajemy po tokenExpired (nie connected — /status
                     zwraca connected = !expired, review #2), a odpowiedź /pixels
                     ma własne flagi connected/tokenExpired z live-walidacji,
                     które mają pierwszeństwo przed "pusta lista" (review #3). ─── */}
                <div className="rounded-md border p-3 space-y-2">
                  {metaOauthLoading ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="w-4 h-4 animate-spin shrink-0" />
                      <span>{t("metaOauth.statusLoading")}</span>
                    </div>
                  ) : metaOauthReady ? (
                    <>
                      <div className="flex items-center gap-2 text-sm">
                        <CheckCircle className="w-4 h-4 text-green-500 shrink-0" />
                        <span>{t("metaOauth.connected")}</span>
                      </div>
                      <div>
                        <Label className="text-xs">{t("metaOauth.pixelSelectLabel")}</Label>
                        {metaPixelsLoading ? (
                          <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
                            <Loader2 className="w-3 h-3 animate-spin" />
                            {t("metaOauth.pixelsLoading")}
                          </div>
                        ) : metaPixelsError ? (
                          <p className="text-xs text-destructive py-2">
                            {t("metaOauth.pixelsError")}
                          </p>
                        ) : metaPixels && (metaPixels.tokenExpired || !metaPixels.connected) ? (
                          <div className="space-y-2 py-1">
                            <p className="text-xs text-destructive">
                              {metaPixels.tokenExpired
                                ? t("metaOauth.expired")
                                : t("metaOauth.notConnected")}
                            </p>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="w-full"
                              onClick={onMetaConnect}
                              disabled={isConnectingMeta}
                            >
                              <RefreshCw className="w-4 h-4 mr-1" />
                              {t("metaOauth.reconnectButton")}
                            </Button>
                          </div>
                        ) : pixelOptions.length === 0 && !currentPixelOutsideList ? (
                          <p className="text-xs text-muted-foreground py-2">
                            {t("metaOauth.pixelsEmpty")}
                          </p>
                        ) : (
                          <>
                            <Select
                              value={formData.metaPixelId || ""}
                              onValueChange={(v) => setFormData({ ...formData, metaPixelId: v })}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder={t("metaOauth.pixelSelectPlaceholder")} />
                              </SelectTrigger>
                              <SelectContent>
                                {pixelOptions.map((p) => (
                                  <SelectItem key={p.id} value={p.id}>
                                    {p.name} ({p.id})
                                    {p.adAccountName ? ` — ${p.adAccountName}` : ""}
                                  </SelectItem>
                                ))}
                                {currentPixelOutsideList && (
                                  <SelectItem value={formData.metaPixelId}>
                                    {t("metaOauth.manualPixelOption", {
                                      id: formData.metaPixelId,
                                    })}
                                  </SelectItem>
                                )}
                              </SelectContent>
                            </Select>
                            {metaPixels?.truncated && (
                              <p className="text-xs text-muted-foreground mt-1">
                                {t("metaOauth.pixelsTruncated")}
                              </p>
                            )}
                          </>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center gap-2 text-sm">
                        <AlertCircle className="w-4 h-4 text-orange-500 shrink-0" />
                        <span>
                          {metaOauth?.tokenExpired
                            ? t("metaOauth.expired")
                            : t("metaOauth.notConnected")}
                        </span>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={onMetaConnect}
                        disabled={isConnectingMeta}
                      >
                        {isConnectingMeta ? (
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        ) : (
                          <RefreshCw className="w-4 h-4 mr-1" />
                        )}
                        {metaOauth?.tokenExpired
                          ? t("metaOauth.reconnectButton")
                          : t("metaOauth.connectButton")}
                      </Button>
                    </>
                  )}
                </div>

                {/* ─── Zaawansowane: ręczny Pixel ID + token system-usera (fallback).
                     Otwarte domyślnie, gdy OAuth nie działa albo config używa
                     ręcznego tokenu — wtedy to jedyna/właściwa ścieżka (review #12). ─── */}
                <Accordion
                  type="single"
                  collapsible
                  defaultValue={
                    !metaOauthReady || config?.metaAccessToken ? "manual-token" : undefined
                  }
                >
                  <AccordionItem value="manual-token" className="border-none">
                    <AccordionTrigger className="text-xs py-2">
                      {t("metaOauth.advancedTitle")}
                    </AccordionTrigger>
                    <AccordionContent className="space-y-3">
                      <p className="text-xs text-muted-foreground">{t("metaOauth.advancedHint")}</p>
                      <div>
                        <Label className="text-xs">{t("platformCard.fields.metaPixelId")}</Label>
                        <Input
                          placeholder="np. 1234567890123456"
                          value={formData.metaPixelId || ""}
                          onChange={(e) =>
                            setFormData({ ...formData, metaPixelId: e.target.value })
                          }
                        />
                      </div>
                      <div>
                        <Label className="text-xs">
                          {t("platformCard.fields.metaAccessToken")}
                        </Label>
                        <div className="relative">
                          <Input
                            type={showTokens ? "text" : "password"}
                            placeholder="EAABsb... (token system usera)"
                            value={formData.metaAccessToken || ""}
                            onChange={(e) =>
                              setFormData({ ...formData, metaAccessToken: e.target.value })
                            }
                          />
                          <button
                            type="button"
                            className="absolute right-2 top-2 text-muted-foreground"
                            onClick={() => setShowTokens(!showTokens)}
                          >
                            {showTokens ? (
                              <EyeOff className="w-4 h-4" />
                            ) : (
                              <Eye className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {t("platformCard.hints.metaToken")}
                        </p>
                      </div>
                      {config?.metaAccessToken && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="w-full"
                          onClick={() => {
                            // Merge z bieżącym formularzem — usunięcie tokenu nie
                            // może gubić świeżo wybranego pixela (review #10).
                            onSave({ ...buildSaveData(), metaAccessToken: null });
                            setEditing(false);
                          }}
                        >
                          <Trash2 className="w-4 h-4 mr-1 text-red-500" />
                          {t("metaOauth.removeManualToken")}
                        </Button>
                      )}
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>

                <div>
                  <Label className="text-xs">{t("platformCard.fields.metaTestEventCode")}</Label>
                  <Input
                    placeholder="TEST12345"
                    value={formData.metaTestEventCode || ""}
                    onChange={(e) =>
                      setFormData({ ...formData, metaTestEventCode: e.target.value })
                    }
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {t("platformCard.hints.metaTestCode")}
                  </p>
                </div>
              </>
            )}
            {platformKey === "google_ads" && (
              <>
                <div>
                  <Label className="text-xs">{t("platformCard.fields.googleCustomerId")}</Label>
                  <Input
                    placeholder="123-456-7890"
                    value={formData.googleCustomerId || ""}
                    onChange={(e) => setFormData({ ...formData, googleCustomerId: e.target.value })}
                  />
                </div>
                <div>
                  <Label className="text-xs">
                    {t("platformCard.fields.googleConversionAction")}
                  </Label>
                  <Input
                    placeholder="customers/123456/conversionActions/789"
                    value={formData.googleConversionActionId || ""}
                    onChange={(e) =>
                      setFormData({ ...formData, googleConversionActionId: e.target.value })
                    }
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  {t("platformCard.hints.googleOAuth")}
                </p>
              </>
            )}
            {platformKey === "tiktok" && (
              <>
                <div>
                  <Label className="text-xs">{t("platformCard.fields.tiktokPixelId")}</Label>
                  <Input
                    placeholder="np. CXXXXXXXXX"
                    value={formData.tiktokPixelId || ""}
                    onChange={(e) => setFormData({ ...formData, tiktokPixelId: e.target.value })}
                  />
                </div>
                <div>
                  <Label className="text-xs">{t("platformCard.fields.tiktokAccessToken")}</Label>
                  <div className="relative">
                    <Input
                      type={showTokens ? "text" : "password"}
                      placeholder="Token z TikTok Business Center"
                      value={formData.tiktokAccessToken || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, tiktokAccessToken: e.target.value })
                      }
                    />
                    <button
                      type="button"
                      className="absolute right-2 top-2 text-muted-foreground"
                      onClick={() => setShowTokens(!showTokens)}
                    >
                      {showTokens ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">{t("platformCard.fields.tiktokTestEventCode")}</Label>
                  <Input
                    placeholder="TEST12345"
                    value={formData.tiktokTestEventCode || ""}
                    onChange={(e) =>
                      setFormData({ ...formData, tiktokTestEventCode: e.target.value })
                    }
                  />
                </div>
              </>
            )}

            <div className="flex gap-2 pt-2">
              <Button size="sm" className="flex-1" onClick={handleSave} disabled={isSaving}>
                {isSaving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                {t("platformCard.saveButton")}
              </Button>
              <Button variant="outline" size="sm" onClick={() => setEditing(false)}>
                {t("platformCard.cancelButton")}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Tag multi-select (used inside AddMappingDialog filters) ─────────────
// Loads user-defined tags from /api/conversion-tags/tags and exposes a
// checkbox list. The selected slugs are serialized as comma-separated string
// for compatibility with the existing payload parser in handleSubmit.
function TagMultiSelect({
  label,
  hint,
  forUserId,
  value,
  onChange,
}: {
  label: string;
  hint: string;
  forUserId: string | null;
  value: string;
  onChange: (next: string) => void;
}) {
  const qs = forUserId ? `?forUserId=${encodeURIComponent(forUserId)}` : "";
  const { data: tags = [] } = useQuery<
    Array<{ id: string; name: string; slug: string; color: string | null; kind: string }>
  >({
    queryKey: ["conversion-tags", forUserId],
    queryFn: () => apiRequest("GET", `/api/conversion-tags/tags${qs}`),
    staleTime: 30_000,
  });

  // Live GHL tag catalog — powers autocomplete + walidacja czy wpisany tag
  // faktycznie istnieje w GHL location. Cache 60s server-side, 30s client-side.
  const { data: ghlData, isLoading: ghlLoading } = useQuery<{
    tags: { name: string; id: string | null }[];
  }>({
    queryKey: ["conversion-tags-ghl-live", forUserId],
    queryFn: () => apiRequest("GET", `/api/conversion-tags/ghl-tags${qs}`),
    staleTime: 30_000,
  });
  const ghlTags = ghlData?.tags ?? [];
  const ghlTagNamesLower = new Set(ghlTags.map((t) => t.name.toLowerCase()));

  // `value` is the existing comma/newline-separated string in parent state —
  // parse to a Set of slugs so we can toggle checkboxes against it.
  const selected = new Set(
    value
      .split(/[,\n]/)
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean)
  );

  const toggle = (slug: string) => {
    const next = new Set(selected);
    if (next.has(slug)) next.delete(slug);
    else next.add(slug);
    onChange(Array.from(next).join(", "));
  };

  // Validation: dla kazdej selektowanej wartosci sprawdz czy jest w GHL location
  // ALBO match'uje internal slug. Tagi ktore nie matchuja zadnego zrodla → ostrzezenie
  // (literowka albo tag nie istnieje w GHL).
  const internalSlugs = new Set(tags.map((t) => t.slug.toLowerCase()));
  const selectedItems = Array.from(selected).map((v) => ({
    value: v,
    inInternal: internalSlugs.has(v),
    inGhl: ghlTagNamesLower.has(v),
  }));
  const unknownItems = selectedItems.filter((s) => !s.inInternal && !s.inGhl);

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <Label className="text-xs">{label}</Label>
        <Link href="/tags">
          <a className="text-[11px] text-blue-400 hover:underline">Zarządzaj tagami →</a>
        </Link>
      </div>

      {/* Internal tags */}
      {tags.length === 0 ? (
        <div className="text-xs text-muted-foreground border rounded p-3">
          Brak wlasnych tagow.{" "}
          <Link href="/tags">
            <a className="text-blue-400 hover:underline">Stwórz pierwszy →</a>
          </Link>
        </div>
      ) : (
        <div className="border rounded p-2 max-h-40 overflow-y-auto space-y-1">
          {tags.map((tag) => {
            const checked = selected.has(tag.slug);
            return (
              <label
                key={tag.id}
                className={`flex items-center gap-2 px-2 py-1 rounded cursor-pointer hover:bg-white/[0.05] ${checked ? "bg-white/[0.04]" : ""}`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(tag.slug)}
                  className="rounded"
                />
                <Badge
                  className="shrink-0"
                  style={{ backgroundColor: tag.color || "#6b7280", color: "#fff" }}
                >
                  {tag.name}
                </Badge>
                <code className="text-[10px] text-muted-foreground">{tag.slug}</code>
              </label>
            );
          })}
        </div>
      )}

      {/* GHL live tags */}
      {ghlLoading ? (
        <div className="text-[11px] text-muted-foreground">Sprawdzam tagi w GHL...</div>
      ) : ghlTags.length > 0 ? (
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-[11px] uppercase tracking-wider text-emerald-300/80 font-semibold">
              Tagi z Twojego GHL (klik aby dodac)
            </span>
            <span className="text-[10px] text-white/35">{ghlTags.length} tagow</span>
          </div>
          <div className="flex flex-wrap gap-1 border border-emerald-500/15 bg-emerald-500/[0.03] rounded p-2 max-h-32 overflow-y-auto">
            {ghlTags.map((t) => {
              const nameLc = t.name.toLowerCase();
              const checked = selected.has(nameLc);
              return (
                <button
                  key={t.id ?? t.name}
                  type="button"
                  onClick={() => toggle(nameLc)}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] transition-colors ${
                    checked
                      ? "bg-emerald-500/25 border border-emerald-400/60 text-emerald-100"
                      : "bg-white/[0.04] border border-white/[0.08] text-white/70 hover:border-emerald-400/40 hover:bg-emerald-500/[0.08]"
                  }`}
                >
                  {checked && <CheckCircle className="w-2.5 h-2.5" />}
                  <span>{t.name}</span>
                </button>
              );
            })}
          </div>
          <p className="text-[10px] text-emerald-300/70">
            ✨ Pull-mode: apka sama sprawdza te tagi w GHL przy kazdej konwersji. Zero webhook
            konfiguracji.
          </p>
        </div>
      ) : null}

      {/* Walidacja: ostrzezenie jak user wpisal cos czego nie ma w zadnym zrodle */}
      {unknownItems.length > 0 && (
        <div className="rounded-md border border-amber-500/30 bg-amber-500/[0.06] p-2 text-[11px] text-amber-100/85 flex items-start gap-2">
          <AlertCircle className="w-3.5 h-3.5 text-amber-400 mt-0.5 flex-shrink-0" />
          <div>
            <strong>Uwaga:</strong> {unknownItems.length === 1 ? "tag" : "tagi"}{" "}
            <code className="bg-white/10 px-1 rounded">
              {unknownItems.map((u) => u.value).join(", ")}
            </code>{" "}
            {unknownItems.length === 1 ? "nie istnieje" : "nie istnieja"} ani jako Twoj internal
            slug, ani w GHL location. Sprawdz literowke lub stworz tag w GHL.
          </div>
        </div>
      )}

      <p className="text-[11px] text-muted-foreground">{hint}</p>
    </div>
  );
}
